import { atom } from 'recoil';
import dayjs, { Dayjs } from 'dayjs';
import { HeatmapDataType, ScheduledActionOptions, TaskItem } from '@demind-inc/core';
import {
  RecommendationType,
  DataTrendPeriod,
  TaskErrorInfo,
  WearableErrorInfo,
  ViewSwitcherStateType,
  DataMenuItem,
  HomeViewSwitcherType,
  CalendarViewType,
  CoreActionOption,
  CalendarSnackInfo,
} from '../types';
import type { CannyPostOptionnName } from '../hooks/useUpdateCannyPost';
import { recoilPersist } from 'recoil-persist';
import { safeLocalStorage } from '../../utils/safeLocalStorage';

const { persistAtom } = recoilPersist({ storage: safeLocalStorage });
export const selectedDateAtom = atom<Dayjs>({
  key: 'selectedDate',
  default: dayjs(),
});

export const currentStartDateAtom = atom<Dayjs>({
  key: 'currentStartDate',
  default: dayjs(),
});

export const visibleTaskDetailAtom = atom<TaskItem | null>({
  key: 'visibleTaskDetail',
  default: null,
});

export const quickActionTaskCreateAtom = atom<boolean>({
  key: 'quickActionTaskCreate',
  default: false,
});

export const quickActionSleepModalAtom = atom<boolean>({
  key: 'quickActionSleepModal',
  default: false,
});

export const eventsSnackBarAtom = atom<CalendarSnackInfo | null>({
  key: 'eventsSnackBar',
  default: null,
});

export const taskErrorSnackBarAtom = atom<TaskErrorInfo | null>({
  key: 'taskErrorSnackBar',
  default: null,
});

export const generalSnackBarAtom = atom<string | null>({
  key: 'generalSnackBar',
  default: null,
});

export const visibleFeedbackDialogPostNameAtom = atom<CannyPostOptionnName | null>({
  key: 'visibleFeedbackDialogPostName',
  default: null,
});

export const wearableErrorSnackBarAtom = atom<WearableErrorInfo | null>({
  key: 'wearableErrorSnackBar',
  default: null,
});

export const failedSyncTerraUserIdsAtom = atom<string[]>({
  key: 'failedSyncTerraUserIds',
  default: [],
});

export const isFetchingCircadianAtom = atom<boolean>({
  key: 'isFetchingCircadian',
  default: false,
});

export const isFetchingAvgCircadianAtom = atom<boolean>({
  key: 'isFetchingAvgCircadian',
  default: false,
});

export const dataTrendPeriodAtom = atom<DataTrendPeriod>({
  key: 'dataTrendPeriod',
  default: 'daily',
});

export const energyboostOptionsTimelineAtom = atom<ScheduledActionOptions[]>({
  key: 'energyBoost.energyboostOptionsTimeline',
  default: [],
});

export const selectedRecommendationTypeAtom = atom<RecommendationType>({
  key: 'selectedRecommendationType',
  default: 'default',
});

export const selectedEnergyBoostOptionsAtom = atom<ScheduledActionOptions | undefined>({
  key: 'energyBoost.selectedEnergyBoostOptions',
  default: undefined,
});

export const selectedCoreActionModalAtom = atom<CoreActionOption | undefined>({
  key: 'coreActions.selectedCoreActionModal',
  default: undefined,
});
export const viewSwitcherAtom = atom<ViewSwitcherStateType>({
  key: 'viewSwitcher',
  default: 'Home',
});

export const selectedDataMenuItem = atom<DataMenuItem>({
  key: 'selectedDataMenuItem',
  default: 'dataCircadian',
});

export const homeViewSwitcherAtom = atom<HomeViewSwitcherType>({
  key: 'homeViewSwitcher',
  default: 'Todo',
  effects_UNSTABLE: [persistAtom],
});

export const calendarViewSwitcherAtom = atom<CalendarViewType>({
  key: 'calendarViewSwitcher',
  default: 'day',
  effects_UNSTABLE: [persistAtom],
});

const getInitialSidebarOpen = (): boolean => {
  if (typeof window === 'undefined') return true;
  const stored = safeLocalStorage.getItem('sidebarOpen');
  if (stored === null) return true;
  return stored === 'true';
};

export const sidebarOpenAtom = atom<boolean>({
  key: 'sidebarOpen',
  default: getInitialSidebarOpen(),
});
